import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';
import { useToast } from '../../../contexts/ToastContext';
import { useSubscription, getUserTier } from '../../../contexts/SubscriptionContext';
import usePremiumAccess from '../../../hooks/usePremiumAccess';
import { AiStudyGenerationApi } from '../../../services/ai/AiStudyGenerationApi';
import { registerJob, completeJob, failJob } from '../../../hooks/useGenerationJobs';
import DocumentSelectionModal from '../../documents/DocumentSelectionModal';
import MiniGameLoadingOverlay from '../MiniGameLoadingOverlay';
// AiToolEmptyState unused in this tool
import FlashcardHistoryPanel from '../../common/FlashcardHistoryPanel';
import FlashcardDetailView from '../../common/FlashcardDetailView';
import FlashcardViewer from './FlashcardViewer';
import FlashcardsManage from './FlashcardsManage';
import UpgradeModal from '../../common/UpgradeModal';
import './FlashcardsTool.css';
import '../../primitives/Button.css';

// Module-level store for generation results that complete while component is unmounted.
let pendingFlashcardResult = null;

const scoutFlashcardsIcon = '/scout_images/scout_flashcard.png';

/**
 * FlashcardsTool - AI-powered flashcard generation from selected documents
 * 
 * Features:
 * - Multi-document selection via DocumentSelectionModal
 * - 3D flip animation with keyboard navigation
 * - Session-based deck history with inline rename and delete
 * - Progress tracking (viewed/flipped/marked cards with per-deck persistence)
 * - Filter mode for marked cards only
 * - Shuffle with visual feedback + Reset Order
 * - Export: JSON (full/marked), CSV, HTML print view
 * - Auto-advance toggle for rapid review
 * - Keyboard shortcuts (arrows, Space, M, ?, Home/End)
 * - Responsive layout with theme token styling
 * - State persistence when switching tools
 * 
 * Future backend persistence API (not implemented yet):
 * - GET /api/flashcards/decks/:deckId/marks → list of marked card ids
 * - PUT /api/flashcards/decks/:deckId/marks → replace list
 * - POST /api/flashcards/decks (save metadata + cards)
 * - GET /api/flashcards/decks?course_id=...
 * - PATCH /api/flashcards/decks/:id (rename)
 * - DELETE /api/flashcards/decks/:id
 * 
 * Future spaced repetition schema (not implemented yet):
 * - card_id, ease, interval, repetitions, due_at, last_reviewed_at
 */
export default function FlashcardsTool({ 
  selectedCourse, 
  persistedState,
  onStateChange,
  isExpanded = false,
  isPanelExpanded = true,
  onSelectDeck,
  externalSelectedDeck = null,
  externalShowGenerator = false,
  onClearDeckSelection
}) {
  const { user } = useAuth();
  const dataContext = useData();
  const { updateFlashcardDeck, getFlashcardDeck, getDocumentById } = dataContext || {};
  const { addToast } = useToast();
  const { subscription, initialized } = useSubscription();
  const { canCreate, tier: currentTier, initialized: premiumInitialized, previewGating } = usePremiumAccess();
  const containerRef = useRef(null);
  const switchToDeckIdRef = useRef(null);
  
  // Subscription gating state – blocks NEW creation, not history viewing
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isCreationBlocked = (initialized || premiumInitialized) && !canCreate('flashcards');

  // TEMP: debug logging — remove when rolling out premium gating
  useEffect(() => {
    console.log('[FlashcardsTool] Gating state:', {
      initialized,
      premiumInitialized,
      previewGating,
      canCreateFlashcards: canCreate('flashcards'),
      isCreationBlocked,
    });
  }, [initialized, premiumInitialized, previewGating, isCreationBlocked, canCreate]);

  const prevStateRef = useRef(null);
  const isMountedRef = useRef(true);
  const onStateChangeRef = useRef(onStateChange);
  
  // All documents are now selectable — AI service handles both RAG-processed and unprocessed docs
  const requiredStatusIds = [];

  // ============ PERSISTED STATE ============
  // Note: selectedDocumentIds is NOT persisted - always starts fresh
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [numCards, setNumCards] = useState(
    persistedState?.numCards || 20
  );
  const [deck, setDeck] = useState(
    persistedState?.deck || null
  );
  const [originalCards, setOriginalCards] = useState(
    persistedState?.originalCards || []
  );
  const [cards, setCards] = useState(
    persistedState?.cards || []
  );
  const [deckHistory, setDeckHistory] = useState(
    persistedState?.deckHistory || []
  );
  const [marksByDeckId, setMarksByDeckId] = useState(
    persistedState?.marksByDeckId || {}
  );
  const [viewedSet, setViewedSet] = useState(
    () => new Set(persistedState?.viewedArray || [])
  );
  const [flippedSet, setFlippedSet] = useState(
    () => new Set(persistedState?.flippedArray || [])
  );
  const [activeIndex, setActiveIndex] = useState(
    persistedState?.activeIndex || 0
  );
  const [autoAdvanceSeconds, setAutoAdvanceSeconds] = useState(
    persistedState?.autoAdvanceSeconds || 0
  );

  // NEW: Premium redesign persisted state
  const [knownSet, setKnownSet] = useState(
    () => new Set(persistedState?.knownArray || [])
  );
  const [missedSet, setMissedSet] = useState(
    () => new Set(persistedState?.missedArray || [])
  );
  const [autoAdvanceOnKnown, setAutoAdvanceOnKnown] = useState(
    persistedState?.autoAdvanceOnKnown !== undefined ? persistedState.autoAdvanceOnKnown : false
  );
  const [continueBehavior, setContinueBehavior] = useState(
    persistedState?.continueBehavior || 'resume'
  );

  // ============ UI STATE (not persisted) ============
  const [isFlipped, setIsFlipped] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const [showMarkedOnly, setShowMarkedOnly] = useState(false);
  const [loading, setLoading] = useState(
    persistedState?.loading || false
  );
  const [error, setError] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printMarkedOnly, setPrintMarkedOnly] = useState(false);
  const [printLayout, setPrintLayout] = useState('2col');
  const [undoMarksSnapshot, setUndoMarksSnapshot] = useState(null);
  const [showUndoToast, setShowUndoToast] = useState(false);

  // NEW: Premium redesign UI state
  const [showManageModal, setShowManageModal] = useState(false);
  const [reviewMode, setReviewMode] = useState(null); // null | 'marked_review'
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showDeckHistoryView, setShowDeckHistoryView] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [selectedDeckForDetail, setSelectedDeckForDetail] = useState(null);
  const overlayRef = useRef(null);
  const [overlayHeight, setOverlayHeight] = useState(0);

  // ============ HELPER FUNCTIONS ============
  
  // Generate stable card key
  const getCardKey = useCallback((card, index) => {
    if (!deck) return `temp:${index}`;
    const deckId = deck.deck_id || deck.id || 'temp';
    const cardId = card.flashcard_id || card.id || index;
    return `${deckId}:${cardId}`;
  }, [deck]);

  // Get current deck's marked set
  const getCurrentMarkedSet = useCallback(() => {
    if (!deck) return new Set();
    const deckKey = deck.deck_id ? String(deck.deck_id) : 'temp';
    const stored = marksByDeckId[deckKey];
    if (!stored) return new Set();
    if (stored instanceof Set) return stored;
    if (Array.isArray(stored)) return new Set(stored);
    // Plain object from JSON deserialization — extract keys or values
    if (typeof stored === 'object') return new Set(Object.keys(stored));
    return new Set();
  }, [deck, marksByDeckId]);

  // Fisher-Yates shuffle
  const shuffle = useCallback((arr) => {
    const result = [...arr];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }, []);

  // CSV escape helper
  const csvEscape = useCallback((value = '') => {
    const s = String(value).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    return `"${s.replace(/"/g, '""')}"`;
  }, []);

  // ============ DERIVED STATE ============
  const markedSet = getCurrentMarkedSet();
  const displayCards = showMarkedOnly 
    ? cards.filter((card, i) => markedSet.has(getCardKey(card, i)))
    : cards;

  // ============ RESPONSIVE LAYOUT ============
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      // Width observation removed - was unused
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Measure overlay height for proper list offset
  useEffect(() => {
    if (isCreating && overlayRef.current) {
      const height = overlayRef.current.offsetHeight;
      setOverlayHeight(height);
    } else {
      setOverlayHeight(0);
    }
  }, [isCreating]);

  // Track mounted state for generation survival across tab switches
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Keep onStateChangeRef in sync
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  // On mount: consume any pending generation result that completed while unmounted
  useEffect(() => {
    if (pendingFlashcardResult) {
      const result = pendingFlashcardResult;
      pendingFlashcardResult = null;
      console.log('[FlashcardsTool] Consuming pending generation result on remount');
      setDeck(result.deck);
      setOriginalCards(result.originalCards);
      setCards(result.cards);
      setActiveIndex(0);
      setIsFlipped(false);
      setViewedSet(new Set());
      setFlippedSet(new Set());
      setIsCreating(false);
      setLoading(false);
      if (result.deckHistoryEntry) {
        setDeckHistory(prev => [result.deckHistoryEntry, ...prev.filter(d => d.id !== result.deckHistoryEntry.id)]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ STATE PERSISTENCE ============
  useEffect(() => {
    if (!onStateChange) return;

    const currentState = {
      numCards,
      deck,
      originalCards,
      cards,
      deckHistory,
      marksByDeckId,
      viewedArray: Array.from(viewedSet),
      flippedArray: Array.from(flippedSet),
      activeIndex,
      autoAdvanceSeconds,
      knownArray: Array.from(knownSet),
      missedArray: Array.from(missedSet),
      autoAdvanceOnKnown,
      continueBehavior,
      loading
    };

    const prevState = prevStateRef.current;
    if (!prevState || JSON.stringify(currentState) !== JSON.stringify(prevState)) {
      prevStateRef.current = currentState;
      onStateChange(currentState);
    }
  }, [selectedDocumentIds, numCards, deck, originalCards, cards, deckHistory, marksByDeckId, viewedSet, flippedSet, activeIndex, autoAdvanceSeconds, knownSet, missedSet, autoAdvanceOnKnown, continueBehavior, loading, onStateChange]);

  // ============ DOCUMENT SELECTION ============
  const handleToggleDocument = useCallback((docId) => {
    setSelectedDocumentIds(prev => {
      const id = String(docId);
      if (prev.includes(id)) {
        return prev.filter(d => d !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleConnectDocuments = useCallback((docIds) => {
    setSelectedDocumentIds(docIds.map(String));
    setShowDocModal(false);
  }, []);

  // ============ DECK SELECTION ============
  const switchToDeck = useCallback(async (deck) => {
    if (!deck) return;
    
    const deckId = deck.id || deck.deck_id;
    switchToDeckIdRef.current = deckId;
    
    try {
      setLoading(true);
      setError(null);
      
      // If the deck doesn't have cards, fetch the full deck from API
      let fullDeck = deck;
      if (!deck.cards || deck.cards.length === 0) {
        console.log('[FlashcardsTool] Fetching full deck with cards:', deckId);
        fullDeck = await getFlashcardDeck(deckId);
        
        // If a newer switchToDeck call was made, abandon this one
        if (switchToDeckIdRef.current !== deckId) return;
        
        console.log('[FlashcardsTool] Full deck response:', fullDeck);
        
        // Try different property names for cards
        const cards = fullDeck.cards || fullDeck.flashcards || [];
        
        if (cards && cards.length > 0) {
          console.log('[FlashcardsTool] Loaded full deck with', cards.length, 'cards');
          fullDeck.cards = cards; // Normalize to .cards property
        } else {
          console.error('[FlashcardsTool] Failed to fetch full deck or deck has no cards. Full deck:', fullDeck);
          setError('Failed to load flashcard deck - no cards found');
          setLoading(false);
          return;
        }
      }
      
      // Ensure deck has an id property (normalize from deck_id if needed)
      if (!fullDeck.id && fullDeck.deck_id) {
        fullDeck.id = fullDeck.deck_id;
      }
      
      console.log('[FlashcardsTool] Setting deck with id:', fullDeck.id, 'cards:', fullDeck.cards?.length);
      
      // Load deck data
      setDeck(fullDeck);
      setOriginalCards(fullDeck.cards || []);
      setCards(fullDeck.cards || []);
      setActiveIndex(0);
      setIsFlipped(false);
      setShowMarkedOnly(false);
      setIsCreating(false); // Close create overlay when viewing deck
      
      // Don't auto-expand - view deck in current layout state
      
      // Call parent callback if provided
      if (onSelectDeck) {
        onSelectDeck(fullDeck);
      }
    } catch (err) {
      console.error('[FlashcardsTool] Error switching to deck:', err);
      setError('Failed to load flashcard deck');
    } finally {
      setLoading(false);
    }
  }, [onSelectDeck, getFlashcardDeck]);

  // Handler for selecting deck from history panel — show detail view
  // Always switch to detail view, even if a deck is being studied
  const handleSelectDeckFromPanel = useCallback((selectedDeck) => {
    // Clear any active study state so the detail view can show
    if (deck) {
      setDeck(null);
      setCards([]);
      setOriginalCards([]);
      setActiveIndex(0);
      setViewedSet(new Set());
      setFlippedSet(new Set());
      setKnownSet(new Set());
      setMissedSet(new Set());
      setIsFlipped(false);
      setShowMarkedOnly(false);
    }
    setSelectedDeckForDetail(selectedDeck);
  }, [deck]);

  // Handler for starting study from the detail view
  const handleStudyDeckFromDetail = useCallback((deck) => {
    switchToDeck(deck);
    setSelectedDeckForDetail(null);
  }, [switchToDeck]);

  // ============ EXTERNAL DECK SELECTION (from AIToolsPage sidebar) ============
  useEffect(() => {
    if (externalSelectedDeck) {
      console.log('[FlashcardsTool] External deck selection received:', externalSelectedDeck);
      // Clear any active study state so the detail view render condition (!deck) is met
      setDeck(null);
      setCards([]);
      setOriginalCards([]);
      setActiveIndex(0);
      setViewedSet(new Set());
      setFlippedSet(new Set());
      setKnownSet(new Set());
      setMissedSet(new Set());
      setIsFlipped(false);
      setShowMarkedOnly(false);
      // Show detail view
      setSelectedDeckForDetail(externalSelectedDeck);
      // Clear the external selection after handling
      if (onClearDeckSelection) {
        onClearDeckSelection();
      }
    }
  }, [externalSelectedDeck, onClearDeckSelection]);

  // ============ EXTERNAL SHOW GENERATOR (from AIToolsPage New button) ============
  useEffect(() => {
    if (externalShowGenerator) {
      console.log('[FlashcardsTool] External show generator triggered');
      // Reset to idle state to show generator
      setDeck(null);
      setCards([]);
      setOriginalCards([]);
      setActiveIndex(0);
      setViewedSet(new Set());
      setFlippedSet(new Set());
      setKnownSet(new Set());
      setMissedSet(new Set());
      setShowMarkedOnly(false);
      setReviewMode(null);
      setSelectedDocumentIds([]); // Clear document selection for fresh start
      // Clear the flag after handling
      if (onStateChange) {
        onStateChange({ ...persistedState, showGenerator: false });
      }
    }
  }, [externalShowGenerator, onStateChange, persistedState]);

  // ============ GENERATION ============
  const handleGenerate = useCallback(async () => {
    // Premium gate: block new creation for non-entitled users
    if (isCreationBlocked) {
      setShowUpgradeModal(true);
      return;
    }
    if (selectedDocumentIds.length === 0) return;
    
    setLoading(true);
    setError(null);

    let genJobId = null;

    // Track flashcard generation start
    const corrId = UsageEventsTracker.startCorrelation('flashcard');
    UsageEventsTracker.recordUserAction('FLASHCARD_GENERATION_STARTED', {
      action: 'generate',
      category: 'ai_tool',
      label: 'flashcards_start',
      metadata: {
        documentCount: selectedDocumentIds.length,
        numCards,
        courseId: selectedCourse?.id || selectedCourse?.endeavorId
      }
    }, { correlationId: corrId });

    try {
      const userId = user?.id || user?.userId || user?.user_id;
      const endeavorId = selectedCourse?.id || selectedCourse?.endeavorId || null;

      // Build a meaningful title from source document names
      let flashcardTitle = `Flashcards - ${new Date().toLocaleDateString()}`;
      try {
        if (selectedDocumentIds.length > 0 && getDocumentById) {
          const firstDoc = await getDocumentById(selectedDocumentIds[0]);
          const firstName = firstDoc?.name || firstDoc?.title || firstDoc?.fileName;
          if (firstName) {
            const truncName = firstName.length > 50 ? firstName.substring(0, 47) + '...' : firstName;
            if (selectedDocumentIds.length === 1) {
              flashcardTitle = `Flashcards - ${truncName} - ${new Date().toLocaleDateString()}`;
            } else {
              flashcardTitle = `Flashcards - ${truncName} + ${selectedDocumentIds.length - 1} more - ${new Date().toLocaleDateString()}`;
            }
          }
        }
      } catch (docErr) {
        console.warn('[FlashcardsTool] Could not resolve document names for title:', docErr);
      }

      const payload = {
        document_ids: selectedDocumentIds.map(Number),
        num_cards: numCards,
        endeavor_id: endeavorId,
        user_id: userId,
        title: flashcardTitle,
        description: `Generated from ${selectedDocumentIds.length} document(s)`
      };

      // Register processing job for sidebar phantom card
      const genJob = registerJob({
        toolType: 'flashcards',
        title: flashcardTitle,
        metadata: {
          documentCount: selectedDocumentIds.length,
          numCards,
        },
      });
      genJobId = genJob.id;

      const result = await AiStudyGenerationApi.generateFlashcards(payload);
      
      // Track successful generation
      UsageEventsTracker.recordUserAction('FLASHCARD_GENERATION_SUCCESS', {
        action: 'complete',
        category: 'ai_tool',
        entityType: 'flashcard_deck',
        entityId: result.deck_id || result.id,
        label: 'flashcards_generated',
        value: result.flashcards?.length || 0,
        metadata: {
          deckId: result.deck_id || result.id,
          cardCount: result.flashcards?.length || 0,
          documentCount: selectedDocumentIds.length
        }
      }, { correlationId: UsageEventsTracker.getCorrelation('flashcard') });
      UsageEventsTracker.clearCorrelation('flashcard');
      
      // Deck is automatically persisted to backend and cached in DataContext
      const newEntry = {
        id: result.deck_id || result.id,
        title: result.title || result.name || 'Untitled Deck',
        cardCount: result.flashcards?.length || 0,
        createdAt: result.createdAt || new Date().toISOString(),
      };

      // Notify history panels to refresh (clear stale cache first)
      if (dataContext?.clearCache) dataContext.clearCache('flashcard-decks');
      window.dispatchEvent(new CustomEvent('flashcards:created', { detail: { deckId: newEntry.id } }));
      if (genJobId) completeJob(genJobId);

      if (isMountedRef.current) {
        setDeck(result);
        setOriginalCards(result.flashcards || []);
        setCards(result.flashcards || []);
        setDeckHistory(prev => [newEntry, ...prev.filter(d => d.id !== newEntry.id)]);
        setActiveIndex(0);
        setIsFlipped(false);
        setViewedSet(new Set());
        setFlippedSet(new Set());
        setIsCreating(false);
        addToast({ intent: 'success', message: '🃏 Flashcards generated successfully!' });
      } else {
        // Component unmounted during generation — stash result for remount
        console.log('[FlashcardsTool] Generation completed while unmounted, stashing result');
        pendingFlashcardResult = {
          deck: result,
          originalCards: result.flashcards || [],
          cards: result.flashcards || [],
          deckHistoryEntry: newEntry,
        };
        // Push state to parent so it persists across tab switches
        if (onStateChangeRef.current) {
          onStateChangeRef.current({
            deck: result,
            originalCards: result.flashcards || [],
            cards: result.flashcards || [],
            numCards,
            deckHistory: [newEntry],
            marksByDeckId: {},
            viewedArray: [],
            flippedArray: [],
            activeIndex: 0,
            autoAdvanceSeconds: 0,
            knownArray: [],
            missedArray: [],
            autoAdvanceOnKnown: false,
            continueBehavior: 'resume',
            loading: false,
          });
        }
      }

    } catch (err) {
      console.error('[FlashcardsTool] Generation error:', err);
      
      // Track generation error
      UsageEventsTracker.recordUserAction('FLASHCARD_GENERATION_FAILED', {
        action: 'error',
        category: 'ai_tool',
        label: 'flashcards_failed',
        metadata: {
          errorMessage: err.message,
          documentCount: selectedDocumentIds.length
        }
      }, { correlationId: UsageEventsTracker.getCorrelation('flashcard') });
      UsageEventsTracker.clearCorrelation('flashcard');
      setError('Failed to generate flashcards. Please try again.');
      if (genJobId) failJob(genJobId, 'Failed to generate flashcards');
    }
    // Don't call setLoading(false) here — the MiniGameLoadingOverlay
    // controls its own dismissal via onReady/onExit callbacks.
    // Calling it here would unmount the overlay and kill any active game.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocumentIds, numCards, user, selectedCourse, addToast, dataContext, isCreationBlocked]);

  // ============ NAVIGATION ============
  const goToPrevious = useCallback(() => {
    setActiveIndex(prev => Math.max(0, prev - 1));
    setIsFlipped(false);
  }, []);

  const goToNext = useCallback(() => {
    setActiveIndex(prev => Math.min(displayCards.length - 1, prev + 1));
    setIsFlipped(false);
  }, [displayCards.length]);

  const goToFirst = useCallback(() => {
    setActiveIndex(0);
    setIsFlipped(false);
  }, []);

  const goToLast = useCallback(() => {
    setActiveIndex(displayCards.length - 1);
    setIsFlipped(false);
  }, [displayCards.length]);

  // ============ CARD FLIP ============
  const toggleFlip = useCallback(() => {
    setIsFlipped(prev => !prev);
  }, []);

  // Track viewed cards
  useEffect(() => {
    if (displayCards.length === 0 || activeIndex >= displayCards.length) return;
    const card = displayCards[activeIndex];
    const key = getCardKey(card, activeIndex);
    setViewedSet(prev => new Set([...prev, key]));
  }, [activeIndex, displayCards, getCardKey]);

  // Track flipped cards when flipping to back
  useEffect(() => {
    if (!isFlipped || displayCards.length === 0 || activeIndex >= displayCards.length) return;
    const card = displayCards[activeIndex];
    const key = getCardKey(card, activeIndex);
    setFlippedSet(prev => new Set([...prev, key]));
  }, [isFlipped, activeIndex, displayCards, getCardKey]);

  // ============ AUTO-ADVANCE ============
  useEffect(() => {
    if (autoAdvanceSeconds === 0 || !isFlipped || displayCards.length === 0) return;

    const timeout = setTimeout(() => {
      if (activeIndex < displayCards.length - 1) {
        goToNext();
      }
    }, autoAdvanceSeconds * 1000);

    return () => clearTimeout(timeout);
  }, [autoAdvanceSeconds, isFlipped, activeIndex, displayCards.length, goToNext]);

  // ============ SHUFFLE & RESET ============
  const handleShuffle = useCallback(() => {
    setIsShuffling(true);
    requestAnimationFrame(() => {
      setCards(shuffle(cards));
      setActiveIndex(0);
      setIsFlipped(false);
      setTimeout(() => setIsShuffling(false), 150);
    });
  }, [cards, shuffle]);

  const handleResetOrder = useCallback(() => {
    setCards(originalCards);
    setActiveIndex(0);
    setIsFlipped(false);
  }, [originalCards]);

  // ============ MARKING ============
  const toggleMark = useCallback(() => {
    if (!deck || displayCards.length === 0 || activeIndex >= displayCards.length) return;
    
    const deckKey = deck.deck_id ? String(deck.deck_id) : 'temp';
    const card = displayCards[activeIndex];
    const cardKey = getCardKey(card, activeIndex);

    setMarksByDeckId(prev => {
      const currentSet = prev[deckKey] || new Set();
      const newSet = new Set(currentSet);
      
      if (newSet.has(cardKey)) {
        newSet.delete(cardKey);
      } else {
        newSet.add(cardKey);
      }

      return {
        ...prev,
        [deckKey]: newSet
      };
    });
  }, [deck, displayCards, activeIndex, getCardKey]);

  const handleUndoClearMarks = useCallback(() => {
    if (!undoMarksSnapshot) return;
    
    setMarksByDeckId(prev => ({
      ...prev,
      [undoMarksSnapshot.deckKey]: undoMarksSnapshot.marks
    }));
    
    setUndoMarksSnapshot(null);
    setShowUndoToast(false);
  }, [undoMarksSnapshot]);

  // ============ KNOWN CARDS (NEW) ============
  const toggleKnown = useCallback((action) => {
    if (displayCards.length === 0 || activeIndex >= displayCards.length) return;
    
    const card = displayCards[activeIndex];
    const cardKey = getCardKey(card, activeIndex);

    if (action === 'hit') {
      setKnownSet(prev => new Set(prev).add(cardKey));
      setMissedSet(prev => { const s = new Set(prev); s.delete(cardKey); return s; });
      if (activeIndex < displayCards.length - 1) {
        setTimeout(() => goToNext(), 400);
      }
    } else if (action === 'miss') {
      setMissedSet(prev => new Set(prev).add(cardKey));
      setKnownSet(prev => { const s = new Set(prev); s.delete(cardKey); return s; });
      if (activeIndex < displayCards.length - 1) {
        setTimeout(() => goToNext(), 400);
      }
    } else {
      // 'clear' or legacy toggle
      setKnownSet(prev => { const s = new Set(prev); s.delete(cardKey); return s; });
      setMissedSet(prev => { const s = new Set(prev); s.delete(cardKey); return s; });
    }
  }, [displayCards, activeIndex, getCardKey, goToNext]);

  // ============ REVIEW MODE (NEW) ============
  const handleEnterMarkedReview = useCallback(() => {
    setReviewMode('marked_review');
    setShowMarkedOnly(true);
    setActiveIndex(0);
    setIsFlipped(false);
  }, []);

  const handleExitReview = useCallback(() => {
    setReviewMode(null);
    setShowMarkedOnly(false);
    setActiveIndex(0);
    setIsFlipped(false);
  }, []);

  // ============ EXPORT ============
  const handleExportJSON = useCallback((markedOnly = false) => {
    if (!deck) return;
    
    const cardsToExport = markedOnly 
      ? originalCards.filter((c, i) => markedSet.has(getCardKey(c, i)))
      : originalCards;

    const exportData = {
      deck_id: deck.deck_id,
      title: deck.title,
      description: deck.description,
      num_cards: cardsToExport.length,
      flashcards: cardsToExport,
      generated_at: deck.generated_at || new Date().toISOString(),
      cost_usd: deck.cost_usd
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flashcards_${markedOnly ? 'marked_' : ''}${deck.deck_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [deck, originalCards, markedSet, getCardKey]);

  const handleExportCSV = useCallback((markedOnly = false) => {
    if (!deck) return;
    
    const cardsToExport = markedOnly 
      ? originalCards.filter((c, i) => markedSet.has(getCardKey(c, i)))
      : originalCards;

    const lines = [
      'Front,Back',
      ...cardsToExport.map(c => 
        `${csvEscape(c.front_text || c.frontText || c.front || '')},${csvEscape(c.back_text || c.backText || c.back || '')}`
      )
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flashcards_${markedOnly ? 'marked_' : ''}${deck.deck_id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [deck, originalCards, markedSet, getCardKey, csvEscape]);

  const handlePrint = useCallback(() => {
    if (!deck) return;

    const cardsToExport = printMarkedOnly 
      ? originalCards.filter((c, i) => markedSet.has(getCardKey(c, i)))
      : originalCards;

    const courseName = selectedCourse?.name || '';
    const printWindow = window.open('', '_blank');
    
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${deck.title || 'Flashcards'}</title>
  <style>
    @media print {
      @page { margin: 0.75in; }
    }
    * {
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      color: #000;
      background: #fff;
      padding: 0;
      margin: 0;
    }
    .print-header {
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid #000;
    }
    .print-header h1 {
      margin: 0 0 8px 0;
      font-size: 24px;
    }
    .print-header p {
      margin: 4px 0;
      font-size: 14px;
      color: #666;
    }
    .cards-table {
      width: 100%;
      border-collapse: collapse;
    }
    .cards-table th,
    .cards-table td {
      border: 1px solid #333;
      padding: 12px;
      text-align: left;
      vertical-align: top;
    }
    .cards-table th {
      background: #f0f0f0;
      font-weight: bold;
    }
    .cards-table tbody tr {
      page-break-inside: avoid;
    }
    ${printLayout === '1col' ? `
      .cards-table {
        display: block;
      }
      .cards-table tbody {
        display: block;
      }
      .cards-table tr {
        display: block;
        margin-bottom: 16px;
        border: 1px solid #333;
        page-break-inside: avoid;
      }
      .cards-table th,
      .cards-table td {
        display: block;
        border: none;
        border-bottom: 1px solid #ddd;
      }
      .cards-table th {
        font-weight: bold;
        background: #f0f0f0;
      }
      .cards-table td:last-child {
        border-bottom: none;
      }
    ` : ''}
  </style>
</head>
<body>
  <div class="print-header">
    <h1>${deck.title || 'Flashcards'}</h1>
    ${courseName ? `<p><strong>Course:</strong> ${courseName}</p>` : ''}
    <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
    <p><strong>Cards:</strong> ${cardsToExport.length}</p>
  </div>
  <table class="cards-table">
    <thead>
      <tr>
        <th>${printLayout === '1col' ? 'Front' : 'Front'}</th>
        ${printLayout === '2col' ? '<th>Back</th>' : ''}
      </tr>
    </thead>
    <tbody>
      ${cardsToExport.map(card => {
        const front = card.front_text || card.frontText || card.front || '';
        const back = card.back_text || card.backText || card.back || '';
        return printLayout === '1col' 
          ? `<tr><td><strong>Front:</strong><br/>${front}</td></tr><tr><td><strong>Back:</strong><br/>${back}</td></tr>`
          : `<tr><td>${front}</td><td>${back}</td></tr>`;
      }).join('')}
    </tbody>
  </table>
  <script>
    window.onload = function() { window.print(); };
  </script>
</body>
</html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    setShowPrintModal(false);
  }, [deck, originalCards, markedSet, getCardKey, printMarkedOnly, printLayout, selectedCourse]);

  // ============ KEYBOARD NAVIGATION ============
  const handleKeyDown = useCallback((e) => {
    // Ignore if modifier keys pressed
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    
    // Ignore if focus is in an input
    const target = e.target;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.tagName === 'BUTTON') {
      return;
    }

    switch (e.code) {
      case 'ArrowLeft':
        e.preventDefault();
        goToPrevious();
        break;
      case 'ArrowRight':
        e.preventDefault();
        goToNext();
        break;
      case 'ArrowUp':
      case 'ArrowDown':
        e.preventDefault();
        toggleFlip();
        break;
      case 'Space':
        e.preventDefault();
        toggleFlip();
        break;
      case 'Home':
        e.preventDefault();
        goToFirst();
        break;
      case 'End':
        e.preventDefault();
        goToLast();
        break;
      case 'KeyM':
        e.preventDefault();
        toggleMark();
        break;
      case 'Slash':
        if (e.shiftKey) { // ? key
          e.preventDefault();
          setShowShortcutsHelp(true);
        }
        break;
      case 'Escape':
        if (showShortcutsHelp) {
          e.preventDefault();
          setShowShortcutsHelp(false);
        }
        break;
      default:
        break;
    }
  }, [goToPrevious, goToNext, toggleFlip, goToFirst, goToLast, toggleMark, showShortcutsHelp]);

  // ============ NEW DECK ============
  const handleNewDeck = useCallback(() => {
    if (isCreationBlocked) {
      setShowUpgradeModal(true);
      return;
    }
    // Clear any pending generation result
    pendingFlashcardResult = null;

    // Clear state to start a new deck
    setDeck(null);
    setOriginalCards([]);
    setCards([]);
    setActiveIndex(0);
    setIsFlipped(false);
    setViewedSet(new Set());
    setFlippedSet(new Set());
    setShowMarkedOnly(false);
    setError(null);
    setLoading(false);
    setIsCreating(true);
    setSelectedDeckForDetail(null);
    
    // Don't expand desk - let creation happen in current layout state
  }, [isCreationBlocked]);

  // ============ TITLE EDITING ============
  const handleStartEditTitle = useCallback(() => {
    setEditedTitle(deck?.title || 'Untitled Flashcard Set');
    setIsEditingTitle(true);
  }, [deck]);

  const handleSaveTitle = useCallback(async () => {
    if (!deck) return;
    
    const newTitle = editedTitle.trim() || 'Untitled Flashcard Set';
    
    // Update deck with new title
    const updatedDeck = {
      ...deck,
      title: newTitle
    };
    
    setDeck(updatedDeck);
    setIsEditingTitle(false);
    
    // Update in backend via DataContext
    try {
      await updateFlashcardDeck(deck.id || deck.deck_id, { title: newTitle });
    } catch (err) {
      console.error('Failed to update deck title:', err);
    }
  }, [deck, editedTitle, updateFlashcardDeck]);

  const handleCancelEditTitle = useCallback(() => {
    setIsEditingTitle(false);
    setEditedTitle('');
  }, []);

  // ============ RENDER HELPERS ============

  const renderDeckHistoryView = () => {
    return (
      <div className="flashcards-deck-history-full">
        <div className="flashcards-deck-history-full__header">
          <button 
            className="ui-btn ui-btn--ghost"
            onClick={() => setShowDeckHistoryView(false)}
          >
            ← Back
          </button>
          <h2 className="flashcards-deck-history-full__title">Previous Decks</h2>
        </div>
        
        {deckHistory.length === 0 ? (
          <div className="flashcards-deck-history-full__empty">
            <p>No previous decks found.</p>
            <button 
              className="ui-btn ui-btn--primary"
              onClick={() => {
                if (isCreationBlocked) { setShowUpgradeModal(true); return; }
                setShowDeckHistoryView(false);
                setShowDocModal(true);
              }}
            >
              Create First Deck
            </button>
          </div>
        ) : (
          <div className="flashcards-deck-history-full__list">
            {deckHistory.map((historyItem) => (
              <button
                key={historyItem.deck_id}
                className="flashcards-deck-item flashcards-deck-item--large"
                onClick={() => {
                  switchToDeck(historyItem);
                  setShowDeckHistoryView(false);
                }}
              >
                <div className="flashcards-deck-item__info">
                  <div className="flashcards-deck-item__title">{historyItem.title}</div>
                  <div className="flashcards-deck-item__meta">
                    {historyItem.num_cards} cards • {historyItem.docCount} document{historyItem.docCount !== 1 ? 's' : ''}
                  </div>
                  <div className="flashcards-deck-item__date">
                    {new Date(historyItem.generatedAt).toLocaleString()}
                  </div>
                </div>
                <div className="flashcards-deck-item__arrow">→</div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDeckViewer = () => {
    const currentCard = displayCards[activeIndex];
    const currentCardKey = currentCard ? getCardKey(currentCard, activeIndex) : null;
    const isCurrentMarked = currentCardKey ? markedSet.has(currentCardKey) : false;
    const isCurrentKnown = currentCardKey ? (knownSet.has(currentCardKey) ? true : missedSet.has(currentCardKey) ? false : null) : null;

    return (
      <div className="flashcards-deck-viewer">
        {/* Header — matches chat workspace header style */}
        <div className="fh-header">
          {/* Row 1: Context header */}
          <div className="fh-header__context-row">
            <div className="fh-header__left">
              {isEditingTitle ? (
                <div className="fh-title-edit">
                  <input
                    type="text"
                    className="fh-title-edit__input"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveTitle();
                      } else if (e.key === 'Escape') {
                        handleCancelEditTitle();
                      }
                    }}
                    onBlur={handleSaveTitle}
                    autoFocus
                    maxLength={100}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className="fh-header__title-btn"
                  onClick={handleStartEditTitle}
                  title="Click to rename"
                >
                  <span className="fh-header__title-icon" aria-hidden="true">📇</span>
                  <span className="fh-header__title-text">{deck?.title || 'Flashcards'}</span>
                </button>
              )}
              <div className="fh-header__meta">
                <span>{originalCards.length} card{originalCards.length !== 1 ? 's' : ''}</span>
                {deck?.generatedAt && (
                  <span className="fh-header__meta-sep">•</span>
                )}
                {deck?.generatedAt && (
                  <span>Generated {new Date(deck.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                )}
              </div>
            </div>
            <div className="fh-header__right">
              <div className="fh-settings-dropdown-container">
                <button
                  className="ui-btn ui-btn--ghost ui-btn--sm fh-settings-btn"
                  onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  aria-label="Settings"
                >
                  ⚙︎
                </button>
              {showSettingsDropdown && (
                <div className="fh-settings-dropdown">
                  <button
                    className="fh-settings-dropdown__item"
                    onClick={() => {
                      handleNewDeck();
                      setShowSettingsDropdown(false);
                    }}
                  >
                    New Deck
                  </button>
                  <button
                    className="fh-settings-dropdown__item"
                    onClick={() => {
                      setShowManageModal(true);
                      setShowSettingsDropdown(false);
                    }}
                  >
                    Manage Deck…
                  </button>
                  <div className="fh-settings-dropdown__divider" />
                  <button
                    className="fh-settings-dropdown__item"
                    onClick={() => {
                      handleExportJSON(false);
                      setShowSettingsDropdown(false);
                    }}
                  >
                    Export JSON
                  </button>
                  <button
                    className="fh-settings-dropdown__item"
                    onClick={() => {
                      handleExportCSV(false);
                      setShowSettingsDropdown(false);
                    }}
                  >
                    Export CSV
                  </button>
                  <button
                    className="fh-settings-dropdown__item"
                    onClick={() => {
                      setShowPrintModal(true);
                      setShowSettingsDropdown(false);
                    }}
                  >
                    Print Flashcards
                  </button>
                </div>
              )}
            </div>
          </div>
          </div>

          {/* Row 2: Study HUD */}
          {displayCards.length > 0 && (
            <div className="fh-header__study-hud">
              <div className="fh-header__hud-text">
                Card {activeIndex + 1} of {displayCards.length}
              </div>
              <div className="fh-header__hud-bar">
                <div
                  className="fh-header__hud-fill"
                  style={{ width: `${displayCards.length > 0 ? ((activeIndex + 1) / displayCards.length) * 100 : 0}%` }}
                />
              </div>
              <div className="fh-header__hud-chips">
                <span>Viewed: {viewedSet.size}</span>
                <span className="fh-header__hud-sep">•</span>
                <span>Flipped: {flippedSet.size}</span>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="fh-header__divider" />

        {/* Card Content */}
        {displayCards.length === 0 ? (
              <div className="fh-empty-marked">
                <div className="fh-empty-icon">⭐</div>
                <h3>No marked cards yet</h3>
                <p>Press M or click ⭐ to mark cards for review.</p>
                <button 
                  className="ui-btn ui-btn--neutral"
                  onClick={handleExitReview}
                >
                  Show All Cards
                </button>
              </div>
            ) : (
              <FlashcardViewer
                card={currentCard}
                isFlipped={isFlipped}
                onFlip={toggleFlip}
                currentIndex={activeIndex}
                totalCards={displayCards.length}
                viewedCount={viewedSet.size}
                flippedCount={flippedSet.size}
                markedCount={markedSet.size}
                knownCount={knownSet.size}
                isMarked={isCurrentMarked}
                isKnown={isCurrentKnown}
                onPrevious={goToPrevious}
                onNext={goToNext}
                onShuffle={handleShuffle}
                onToggleMarked={toggleMark}
                onToggleKnown={toggleKnown}
                onShowShortcuts={() => setShowShortcutsHelp(true)}
                isExpanded={isExpanded}
                isShuffled={isShuffled}
                onResetOrder={handleResetOrder}
                reviewMode={reviewMode}
                onExitReview={handleExitReview}
                onEnterReview={handleEnterMarkedReview}
                isShuffling={isShuffling}
              />
            )}

        {/* Manage Deck Modal */}
        {showManageModal && (
          <div className="fh-modal-overlay" onClick={() => setShowManageModal(false)}>
            <div className="fh-modal fh-modal--manage" onClick={(e) => e.stopPropagation()}>
              <div className="fh-modal-header">
                <h3>Manage Deck</h3>
                <button
                  className="fh-modal-close"
                  onClick={() => setShowManageModal(false)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="fh-modal-body">
                <FlashcardsManage
                  onNewDeck={() => { handleNewDeck(); setShowManageModal(false); }}
                  onRegenerate={() => { setShowManageModal(false); }}
                  onDeleteDeck={() => {
                    console.log('Delete deck via FlashcardHistoryPanel in collapsed mode');
                    setShowManageModal(false);
                  }}
                  onExportJSON={(m) => { handleExportJSON(m); setShowManageModal(false); }}
                  onExportCSV={(m) => { handleExportCSV(m); setShowManageModal(false); }}
                  onPrint={() => { setShowPrintModal(true); setShowManageModal(false); }}
                  markedCount={markedSet.size}
                  autoAdvanceSeconds={autoAdvanceSeconds}
                  onAutoAdvanceChange={setAutoAdvanceSeconds}
                  autoAdvanceOnKnown={autoAdvanceOnKnown}
                  onAutoAdvanceOnKnownChange={setAutoAdvanceOnKnown}
                  continueBehavior={continueBehavior}
                  onContinueBehaviorChange={setContinueBehavior}
                  canRegenerate={selectedDocumentIds.length > 0}
                  isExpanded={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============ MODALS ============
  const renderShortcutsModal = () => {
    if (!showShortcutsHelp) return null;

    return (
      <div className="fh-modal-overlay" onClick={() => setShowShortcutsHelp(false)}>
        <div className="fh-modal" onClick={(e) => e.stopPropagation()}>
          <div className="fh-modal-header">
            <h3>Keyboard Shortcuts</h3>
            <button 
              className="fh-modal-close"
              onClick={() => setShowShortcutsHelp(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="fh-modal-body">
            <div className="fh-shortcut-list">
              <div className="fh-shortcut-row">
                <kbd>←</kbd> <kbd>→</kbd>
                <span>Previous / Next card</span>
              </div>
              <div className="fh-shortcut-row">
                <kbd>↑</kbd> <kbd>↓</kbd>
                <span>Flip card</span>
              </div>
              <div className="fh-shortcut-row">
                <kbd>Space</kbd>
                <span>Flip card</span>
              </div>
              <div className="fh-shortcut-row">
                <kbd>M</kbd>
                <span>Mark / Unmark card</span>
              </div>
              <div className="fh-shortcut-row">
                <kbd>Home</kbd> <kbd>End</kbd>
                <span>First / Last card</span>
              </div>
              <div className="fh-shortcut-row">
                <kbd>?</kbd>
                <span>Show this help</span>
              </div>
              <div className="fh-shortcut-row">
                <kbd>Esc</kbd>
                <span>Close help</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };



  const renderPrintModal = () => {
    if (!showPrintModal) return null;

    return (
      <div className="fh-modal-overlay" onClick={() => setShowPrintModal(false)}>
        <div className="fh-modal" onClick={(e) => e.stopPropagation()}>
          <div className="fh-modal-header">
            <h3>Print Options</h3>
            <button 
              className="fh-modal-close"
              onClick={() => setShowPrintModal(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="fh-modal-body">
            <label className="fh-print-option fh-print-checkbox">
              <input 
                type="checkbox"
                checked={printMarkedOnly}
                onChange={(e) => setPrintMarkedOnly(e.target.checked)}
                disabled={markedSet.size === 0}
              />
              <span className="fh-checkbox-custom"></span>
              <span className="fh-checkbox-label">Marked cards only {markedSet.size > 0 ? `(${markedSet.size})` : '(none)'}</span>
            </label>
            <div className="fh-print-option fh-print-layout">
              <span className="fh-layout-label">Layout:</span>
              <label className="fh-radio-option">
                <input 
                  type="radio"
                  name="printLayout"
                  value="2col"
                  checked={printLayout === '2col'}
                  onChange={(e) => setPrintLayout(e.target.value)}
                />
                <span className="fh-radio-custom"></span>
                <span>Two-column</span>
              </label>
              <label className="fh-radio-option">
                <input 
                  type="radio"
                  name="printLayout"
                  value="1col"
                  checked={printLayout === '1col'}
                  onChange={(e) => setPrintLayout(e.target.value)}
                />
                <span className="fh-radio-custom"></span>
                <span>One-column</span>
              </label>
            </div>
          </div>
          <div className="fh-modal-footer">
            <button 
              className="ui-btn ui-btn--neutral"
              onClick={() => setShowPrintModal(false)}
            >
              Cancel
            </button>
            <button 
              className="ui-btn ui-btn--primary"
              onClick={handlePrint}
            >
              Print
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderUndoToast = () => {
    if (!showUndoToast) return null;

    return (
      <div className="fh-undo-toast">
        <span>Cleared marks</span>
        <button 
          className="ui-btn ui-btn--ghost ui-btn--sm"
          onClick={handleUndoClearMarks}
        >
          Undo
        </button>
      </div>
    );
  };

  // ============ MAIN RENDER ============
  // NOTE: We no longer block the entire tool for non-premium users.
  // History viewing is always allowed; only new creation is gated via
  // handleGenerate → isCreationBlocked → showUpgradeModal.
  
  const isShuffled = JSON.stringify(cards) !== JSON.stringify(originalCards);

  // Show deck history view
  if (showDeckHistoryView) {
    return (
      <div className="flashcards-tool" ref={containerRef}>
        {renderDeckHistoryView()}
        {/* Paywall modal – must be in EVERY return path */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentTier={currentTier}
          requiredTier="basic"
          featureName="Flashcard Generator"
          featureDescription="Create AI-powered flashcards from your course documents and materials."
          triggerSource="quiz"
        />
      </div>
    );
  }

  // ============ LAYOUT RENDERING ============
  // EXPANDED MODE: 2-column layout (history left + workspace right)
  if (isPanelExpanded) {
    return (
      <div className="flashcards-tool flashcards-tool--expanded" ref={containerRef}>
        <div className="flashcards-tool-layout flashcards-tool-layout--expanded">
          {/* LEFT COLUMN: Flashcard History Panel */}
          <div className="flashcards-tool-sidebar">
            <FlashcardHistoryPanel
              currentDeckId={selectedDeckForDetail?.deck_id || selectedDeckForDetail?.id || deck?.id || deck?.deck_id}
              onSelectDeck={handleSelectDeckFromPanel}
              onNewDeck={handleNewDeck}
              showNewButton={false}
            />
          </div>

          {/* RIGHT COLUMN: Workspace (Generator or Viewer) */}
          <div className="flashcards-tool-workspace">
            {/* Loading state - fills workspace */}
            {loading && (
              <div className="flashcards-loading-state flashcards-loading-state--expanded">
                <MiniGameLoadingOverlay
                  loadStatus={error ? 'error' : (deck ? 'ready' : 'loading')}
                  contentType="flashcards"
                  onReady={() => setLoading(false)}
                  onRetry={handleGenerate}
                  onExit={() => {
                    setLoading(false);
                    setError(null);
                  }}
                  canAbort={false}
                  errorMessage={error || "Couldn't load flashcards. Check connection."}
                  isCompact={false}
                />
              </div>
            )}

            {/* Detail View - show when a deck is selected from panel */}
            {!loading && !deck && selectedDeckForDetail && (
              <FlashcardDetailView
                key={selectedDeckForDetail.deck_id || selectedDeckForDetail.id}
                deck={selectedDeckForDetail}
                onStudyDeck={handleStudyDeckFromDetail}
                onBack={() => setSelectedDeckForDetail(null)}
              />
            )}

            {/* Generator mode - show when no active deck and no detail */}
            {!loading && !deck && !selectedDeckForDetail && (
              <div className="quizzesExpandedLayout">
                {/* Left Column: Scout Hero */}
                <div className="quizzesScoutHero">
                  <img 
                    src={scoutFlashcardsIcon}
                    alt="Scout Flashcards"
                    className="quizzesScoutHero__img"
                  />
                </div>

                {/* Right Column: Create Flashcards Panel */}
                <div className="createQuizPanel">
                  {/* Panel Header */}
                  <div className="createQuizPanelHeader">
                    <div className="createQuizPanelHeader__icon">🗂️</div>
                    <h3 className="createQuizPanelHeader__title">Create New Deck</h3>
                    <p className="createQuizPanelHeader__subtitle">
                      Study less. Remember more. Generate flashcards from your course documents.
                    </p>
                    
                    {/* Documents Control - centered when no docs selected */}
                    {selectedDocumentIds.length === 0 && (
                      <div className="createQuizPanelHeader__docs">
                        <button
                          className="ui-btn ui-btn--primary selectDocumentsBtn"
                          onClick={() => { if (isCreationBlocked) { setShowUpgradeModal(true); return; } setShowDocModal(true); }}
                        >
                          📄 Select Documents
                        </button>
                        <p className="createQuizPanelHeader__hint">Documents: none selected</p>
                      </div>
                    )}
                  </div>

                  {/* Panel Controls - only show when docs selected */}
                  {selectedDocumentIds.length > 0 && (
                    <div className="createQuizPanelControls">
                      {/* Documents Selected - Step Complete */}
                      <div className="fc-step-complete">
                        <div className="fc-step-complete__header">
                          <span className="fc-step-complete__check">✓</span>
                          <span className="fc-step-complete__label">Documents Selected</span>
                        </div>
                        <div className="fc-step-complete__detail">
                          <span className="fc-step-complete__desc">
                            {selectedDocumentIds.length} document{selectedDocumentIds.length !== 1 ? 's' : ''} from your course materials
                          </span>
                          <button 
                            className="ui-btn ui-btn--sm ui-btn--ghost fc-step-complete__edit"
                            onClick={() => { if (isCreationBlocked) { setShowUpgradeModal(true); return; } setShowDocModal(true); }}
                          >
                            Edit
                          </button>
                        </div>
                      </div>

                      {/* Number of Cards Slider */}
                      <div className="control-group fc-slider-group">
                        <div className="fc-slider-header">
                          <label className="control-label" htmlFor="flashcards-num">
                            Number of Cards
                          </label>
                          <span className="fc-slider-value">{numCards}</span>
                        </div>
                        <input
                          id="flashcards-num"
                          type="range"
                          min="5"
                          max="100"
                          step="5"
                          value={numCards}
                          onChange={(e) => setNumCards(Number(e.target.value))}
                          className="quiz-slider"
                        />
                      </div>

                      <p className="fc-generate-helper">
                        AI will generate flashcards from the selected documents.
                      </p>
                      <button
                        className="ui-btn ui-btn--primary quiz-generate-btn"
                        onClick={handleGenerate}
                        disabled={loading}
                      >
                        ✨ Generate Flashcards
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Active Deck View - show when deck exists */}
            {!loading && deck && cards.length > 0 && (
              <div className="flashcards-viewer-container" onKeyDown={handleKeyDown}>
                {renderDeckViewer()}
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        {showDocModal && (
          <DocumentSelectionModal
            isOpen={showDocModal}
            selectedDocuments={selectedDocumentIds}
            onToggleDocument={handleToggleDocument}
            onConnectDocuments={handleConnectDocuments}
            onClose={() => setShowDocModal(false)}
            requiredStatusIds={requiredStatusIds}
            title="Select Documents for Flashcards"
            description="Choose processed documents to generate flashcards from."
          />
        )}
        {renderShortcutsModal()}
        {renderPrintModal()}
        {renderUndoToast()}

        {/* Paywall modal – must be in EVERY return path */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentTier={currentTier}
          requiredTier="basic"
          featureName="Flashcard Generator"
          featureDescription="Create AI-powered flashcards from your course documents and materials."
          triggerSource="quiz"
        />
      </div>
    );
  }

  // COLLAPSED MODE: Show FlashcardHistoryPanel only
  console.log('[FlashcardsTool COLLAPSED MODE] isPanelExpanded:', isPanelExpanded, 'deck:', deck?.id, 'cards:', cards?.length, 'loading:', loading);
  
  return (
    <div className="flashcards-tool flashcards-tool--collapsed" ref={containerRef}>
      {/* Header Row */}
      <div className="flashcard-tool-header-row">
        <h2 className="flashcard-tool-header-title">Flashcards</h2>
        
        <div className="flashcard-tool-header-actions">
          {!deck && (
            <input
              type="text"
              placeholder="Search..."
              className="flashcard-search-input"
              value=""
              readOnly
            />
          )}
          
          {deck && (
            <div className="flashcard-settings-dropdown">
              <button
                className="ui-btn ui-btn--ghost ui-btn--icon flashcard-settings-btn"
                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                title="Settings"
              >
                ⚙
              </button>
              {showSettingsDropdown && (
                <>
                  <div 
                    className="flashcard-settings-backdrop" 
                    onClick={() => setShowSettingsDropdown(false)}
                  />
                  <div className="flashcard-settings-menu">
                    <button
                      className="flashcard-settings-menu-item"
                      onClick={() => {
                        handleNewDeck();
                        setShowSettingsDropdown(false);
                      }}
                    >
                      New Deck
                    </button>
                    <div className="flashcard-settings-menu-divider" />
                    <button
                      className="flashcard-settings-menu-item"
                      onClick={() => {
                        handleExportJSON(false);
                        setShowSettingsDropdown(false);
                      }}
                    >
                      Export JSON
                    </button>
                    <button
                      className="flashcard-settings-menu-item"
                      onClick={() => {
                        handleExportCSV(false);
                        setShowSettingsDropdown(false);
                      }}
                    >
                      Export CSV
                    </button>
                    <button
                      className="flashcard-settings-menu-item"
                      onClick={() => {
                        setShowPrintModal(true);
                        setShowSettingsDropdown(false);
                      }}
                    >
                      Print Flashcards
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          
          <button
            className="ui-btn ui-btn--primary ui-btn--icon"
            onClick={handleNewDeck}
            title="Create New Flashcard Deck"
          >
            +
          </button>
        </div>
      </div>

      {/* Body - Contains hero and history list */}
      <div 
        className="flashcard-tool-body"
        style={{ '--create-overlay-offset': `${overlayHeight + 12}px` }}
      >
        {/* Loading State */}
        {loading && (
          <div className="flashcards-loading-state-collapsed">
            <MiniGameLoadingOverlay
              loadStatus={error ? 'error' : (deck ? 'ready' : 'loading')}
              contentType="flashcards"
              onReady={() => setLoading(false)}
              onRetry={handleGenerate}
              onExit={() => {
                setLoading(false);
                setError(null);
              }}
              canAbort={false}
              errorMessage={error || "Couldn't load flashcards. Check connection."}
              isCompact={true}
            />
          </div>
        )}

        {/* Create Flashcard Overlay */}
        <div 
          ref={overlayRef}
          className={`create-overlay ${isCreating && !deck ? 'create-overlay--open' : ''}`}
        >
          {isCreating && !deck && (
            <div className="create-overlay-card">
              <div className="create-overlay-header">
                <h3>Create New Flashcard Deck</h3>
                <button 
                  className="ui-btn ui-btn--icon create-overlay-close"
                  onClick={() => {
                    setIsCreating(false);
                    setLoading(false);
                    setError(null);
                  }}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              
              <div className="create-overlay-content">
                <div className="quiz-builder-panel quiz-builder-panel--compact">
                  <div className="quiz-settings">
                    {/* Documents Selection */}
                    {selectedDocumentIds.length === 0 ? (
                      <div className="control-group">
                        <label className="control-label">Documents</label>
                        <button 
                          className="ui-btn ui-btn--neutral"
                          onClick={() => { if (isCreationBlocked) { setShowUpgradeModal(true); return; } setShowDocModal(true); }}
                        >
                          📄 Select Documents
                        </button>
                      </div>
                    ) : (
                      <div className="fc-step-complete fc-step-complete--compact">
                        <div className="fc-step-complete__header">
                          <span className="fc-step-complete__check">✓</span>
                          <span className="fc-step-complete__label">Documents Selected</span>
                        </div>
                        <div className="fc-step-complete__detail">
                          <span className="fc-step-complete__desc">
                            {selectedDocumentIds.length} document{selectedDocumentIds.length !== 1 ? 's' : ''}
                          </span>
                          <button 
                            className="ui-btn ui-btn--sm ui-btn--ghost fc-step-complete__edit"
                            onClick={() => { if (isCreationBlocked) { setShowUpgradeModal(true); return; } setShowDocModal(true); }}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Number of Cards */}
                    <div className="control-group fc-slider-group">
                      <div className="fc-slider-header">
                        <label className="control-label" htmlFor="num-cards-collapsed">
                          Number of Cards
                        </label>
                        <span className="fc-slider-value">{numCards}</span>
                      </div>
                      <input
                        id="num-cards-collapsed"
                        type="range"
                        min="5"
                        max="100"
                        step="5"
                        value={numCards}
                        onChange={(e) => setNumCards(Number(e.target.value))}
                        className="quiz-slider"
                      />
                    </div>
                  </div>

                  <p className="fc-generate-helper">
                    AI will generate flashcards from the selected documents.
                  </p>
                  <button
                    className="ui-btn ui-btn--primary quiz-generate-btn"
                    onClick={handleGenerate}
                    disabled={selectedDocumentIds.length === 0 || loading}
                  >
                    {loading ? 'Generating...' : '✨ Generate Flashcards'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* History Scroll */}
        <div className="flashcard-history-scroll" style={{ paddingTop: 'var(--create-overlay-offset, 0px)' }}>
          {!loading && deck ? (
            /* Active Deck View */
            <>
              {/* Deck Title */}
              <div className="flashcard-collapsed-deck-header">
                {isEditingTitle ? (
                  <div className="flashcard-title-edit">
                    <input
                      type="text"
                      className="flashcard-title-edit__input"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveTitle();
                        } else if (e.key === 'Escape') {
                          handleCancelEditTitle();
                        }
                      }}
                      onBlur={handleSaveTitle}
                      autoFocus
                      maxLength={100}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="fh-header__title-btn"
                    onClick={handleStartEditTitle}
                    title="Click to rename"
                  >
                    <span className="fh-header__title-text">{deck?.title || 'Flashcards'}</span>
                  </button>
                )}
              </div>
              
              <FlashcardViewer
                card={displayCards[activeIndex]}
                isFlipped={isFlipped}
                onFlip={toggleFlip}
                currentIndex={activeIndex}
                totalCards={displayCards.length}
                viewedCount={viewedSet.size}
                flippedCount={flippedSet.size}
                markedCount={markedSet.size}
                knownCount={knownSet.size}
                isMarked={displayCards[activeIndex] ? markedSet.has(getCardKey(displayCards[activeIndex], activeIndex)) : false}
                isKnown={displayCards[activeIndex] ? (knownSet.has(getCardKey(displayCards[activeIndex], activeIndex)) ? true : missedSet.has(getCardKey(displayCards[activeIndex], activeIndex)) ? false : null) : null}
                onPrevious={goToPrevious}
                onNext={goToNext}
                onShuffle={handleShuffle}
                onToggleMarked={toggleMark}
                onToggleKnown={toggleKnown}
                onShowShortcuts={() => setShowShortcutsHelp(true)}
                isExpanded={false}
                isShuffled={JSON.stringify(cards) !== JSON.stringify(originalCards)}
                onResetOrder={handleResetOrder}
                reviewMode={reviewMode}
                onExitReview={() => setReviewMode(null)}
                isShuffling={isShuffling}
                onEnterReview={() => setReviewMode('marked_review')}
              />
            </>
          ) : (
            <>
              {/* Hero Strip */}
              {!isCreating && (
                <div className="flashcard-hero-strip">
                  <img 
                    src="/scout_images/scout_flashcard.png" 
                    alt="Scout Flashcards"
                    className="flashcard-hero-strip__image"
                  />
                </div>
              )}
              
              {/* Flashcard History List */}
              <FlashcardHistoryPanel
                currentDeckId={deck?.id || deck?.deck_id}
                marksByDeckId={marksByDeckId}
                onSelectDeck={handleSelectDeckFromPanel}
                onNewDeck={handleNewDeck}
                hideHeader={true}
                hideSearch={true}
                showSectionHeader={true}
                showNewButton={false}
              />
            </>
          )}
        </div>
      </div>

      {showDocModal && (
        <DocumentSelectionModal
          isOpen={showDocModal}
          selectedDocuments={selectedDocumentIds}
          onToggleDocument={handleToggleDocument}
          onConnectDocuments={handleConnectDocuments}
          onClose={() => setShowDocModal(false)}
          requiredStatusIds={requiredStatusIds}
          title="Select Documents for Flashcards"
          description="Choose processed documents to generate flashcards from."
        />
      )}
      {renderPrintModal()}
      {renderUndoToast()}

      {/* Paywall modal – shown when non-entitled user tries to generate */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={currentTier}
        requiredTier="basic"
        featureName="Flashcard Generator"
        featureDescription="Create AI-powered flashcards from your course documents and materials."
        triggerSource="quiz"
      />
    </div>
  );
}
